import { IconArrowInbox } from 'central-icons/IconArrowInbox';

import { useUserTrialDetails } from '@revelio/auth';
import { MenuItem, RevelioTooltip } from '@revelio/ui';

import {
  downloadData,
  type ExportData,
} from '../Chart/chart-menu/data-download/download-plot-data';
import { TALENT_DISCOVERY_VALID_COLUMNS } from '../Chart/Table/talent-discovery-columns';
import { getTableColumnHeader } from '../Chart/Table/utils/table-helpers';
import { useFileName } from '../Chart/utils/useFileName';
import {
  type TableRow,
  useTableSelection,
} from '../TableSelectionProvider/TableSelectionProvider';

export const buildXlsxRows = (rows: TableRow[]): ExportData => {
  const columns = TALENT_DISCOVERY_VALID_COLUMNS.filter((key) =>
    rows.some((row) => key in row)
  );

  return rows.map((row) =>
    Object.fromEntries(
      columns.map((key) => {
        const val = row[key];
        const cell = (() => {
          if (typeof val === 'string' || typeof val === 'number') return val;
          if (typeof val === 'boolean') return String(val);
          return '';
        })();
        return [getTableColumnHeader(key), cell];
      })
    )
  );
};

export const TalentDiscoverySelectedDataExportMenuItem = () => {
  const { selectedRows } = useTableSelection();
  const { isTrialUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });
  const fileName = useFileName({ chartTitle: 'Selected' });

  const hasSelection = (selectedRows?.length ?? 0) > 0;
  const isDisabled = isTrialUser || !hasSelection;

  const tooltipLabel = (() => {
    if (isTrialUser) return 'This feature is not available for trial users';
    return 'Select rows from the table to download';
  })();

  const triggerDownload = () => {
    if (!selectedRows || selectedRows.length === 0) return;
    if (isTrialUser) return;

    downloadData(buildXlsxRows(selectedRows), fileName);
  };

  const menuItem = (
    <MenuItem
      icon={<IconArrowInbox size={16} />}
      onClick={triggerDownload}
      disabled={isDisabled}
    >
      {hasSelection
        ? `Download Selected (${selectedRows?.length})`
        : 'Download Selected'}
    </MenuItem>
  );

  if (!isDisabled) return menuItem;

  return (
    <RevelioTooltip label={tooltipLabel} placement="bottom">
      <span>{menuItem}</span>
    </RevelioTooltip>
  );
};
